import React, { useRef, useState } from 'react';
import { PdfWrapper } from '../utility/pdf-wrapper/pdf-wrapper';
import { createPortal } from 'react-dom';
import { Utility } from '../utility/utility';
import JsPDF from 'jspdf';

const pdfRunner = async (pages) => {
  const doc = new JsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: 'portrait',
    compress: true,
  });

  // Adding pages to the PDF
  for (let i = 1; i < pages.length; i++) doc.addPage();

  const imgFormat = Utility.browserSupportsWebP() ? 'webp' : 'jpeg';

  // Function to add links
  const addLinks = (doc, pageIndex, links) => {
    links.forEach((link) => {
      doc.link(link.x, link.y, link.width, link.height, { url: link.url, page: pageIndex + 1 });
    });
  };

  for (let i = pages.length - 1; i >= 0; i--) {
    const page = pages[i];

    // Get link information before rendering HTML
    const links = Array.from(page.querySelectorAll('a')).map((anchor) => {
      const rect = anchor.getBoundingClientRect();
      const pageRect = page.getBoundingClientRect();
      return {
        url: anchor.href,
        x: (rect.left - pageRect.left) * (210 / page.offsetWidth),
        y: (rect.top - pageRect.top) * (297 / page.offsetHeight),
        width: rect.width * (210 / page.offsetWidth),
        height: rect.height * (297 / page.offsetHeight),
      };
    });

    await new Promise((resolve) => {
      doc.html(page, {
        callback: function () {
          addLinks(doc, i, links);
          resolve();
        },
        x: 0,
        y: 297 * i,
        html2canvas: {
          scale: 1 / (page.offsetWidth / 210),
          logging: true,
          useCORS: true,
          allowTaint: true,
          async: true,
        },
        image: { type: imgFormat, quality: 0.98, compression: 'SLOW' },
        autoPaging: 'text',
        width: 210, // A4 width in mm
        height: 297, // A4 height in mm
        windowWidth: page.offsetWidth,
      });
    });

    // // code to split the rendering of the pdf into multiple steps, to avoid browser freeze, work has to be done here
    // stepsToDo.push(async () => {
    //   return new Promise((resolve, reject) => {
    //     doc.html(page, {
    //       callback: function () {
    //         addLinks(doc, i, links);
    //         resolve();
    //       },
    //       x: 0,
    //       y: 297 * i,
    //       html2canvas: {
    //         scale: 1 / (page.offsetWidth / 210),
    //         logging: true,
    //         useCORS: true,
    //         allowTaint: true,
    //         async: true,
    //       },
    //       image: { type: imgFormat, quality: 0.98, compression: 'SLOW' },
    //       autoPaging: 'text',
    //       width: 210, // A4 width in mm
    //       height: 297, // A4 height in mm
    //       windowWidth: page.offsetWidth,
    //     });
    //   });
    // });
  }
  // await startSteps();
  // doc.deletePage(1);
  return doc.output('blob');
};

const PdfAnchorBuilder = () => {
  const [pdfWrapper, setPdfWrapper] = useState(null);

  const PdfAnchor = () => {
    if (!pdfWrapper) return <></>;
    return createPortal(pdfWrapper, document.body);
  };

  return { anchor: PdfAnchor, setWrapper: setPdfWrapper };
};

export const usePdfCreator = (debugMode = false) => {
  const { anchor, setWrapper } = PdfAnchorBuilder();
  const pdfRenderedPromise = useRef(null);
  const pdfGeneratedPromise = useRef(null);
  const pdfTriggerCallback = async (pages) => {
    pdfRenderedPromise.current.resolve(pages);
    return pdfGeneratedPromise.current.promise;
  };

  const createPdf = useRef(async (template) => {
    const callbackPromise = {};
    const generatePromise = {};
    const pdfHasLoadedPromise = {};

    callbackPromise.promise = new Promise((resolve, reject) => {
      callbackPromise.resolve = resolve;
      callbackPromise.reject = reject;
    });
    generatePromise.promise = new Promise((resolve, reject) => {
      generatePromise.resolve = resolve;
      generatePromise.reject = reject;
    });
    pdfHasLoadedPromise.promise = new Promise((resolve, reject) => {
      pdfHasLoadedPromise.resolve = resolve;
      pdfHasLoadedPromise.reject = reject;
    });

    pdfGeneratedPromise.current = generatePromise;
    pdfRenderedPromise.current = callbackPromise;

    setWrapper(
      <PdfWrapper
        isDebug={debugMode}
        pdfFormat={'a4'}
        triggerPdfOutputCallback={pdfTriggerCallback}
        pdfLoadedCallback={pdfHasLoadedPromise.resolve}
      >
        {React.cloneElement(template, { $pdfLoadedCallback: pdfHasLoadedPromise.resolve })}
      </PdfWrapper>,
    );
    const loadScripts = async () => {
      await Utility.loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.3.2/html2canvas.min.js');
      await Utility.loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.0/jspdf.umd.min.js');
    };

    const loadScriptsPromise = loadScripts();
    await pdfHasLoadedPromise.promise;
    const pages = await pdfRenderedPromise.current.promise;
    await loadScriptsPromise;
    const pdfBlob = pages ? await pdfRunner(pages) : null;
    console.log('unloaded');
    generatePromise.resolve(pdfBlob);
    setWrapper(null);
    return pdfBlob;
  });
  return [anchor, createPdf.current];
};
